import React, { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { __ } from '@wordpress/i18n';

// components
import AiJobList from '../AiJobList';
import ModalDialog from '../ModalDialog';

// icons
import MagicAiIcon from '../../icons/MagicAiIcon';

// hooks
import useAiJobs from '../../hooks/useAiJobs';

// service
import CoverService from '../../service/cover-service';

import '../../assets/components/ModalMoodCover.css';

const MOODS = [
    { id: 'Dreamy',    label: 'Dreamy' },
    { id: 'Warm',      label: 'Warm' },
    { id: 'Energetic', label: 'Energetic' },
    { id: 'Cold',      label: 'Cold' },
    { id: 'Dark',      label: 'Dark' },
];

const OPERATIONS = [
    { id: 'cover',   label: __('Cambia mood', 'soundbetween') },
    { id: 'gapfill', label: __('Riempi il vuoto', 'soundbetween') },
];

const DEFAULT_STRENGTH = 0.7;

const ModalMoodCover = ({ visible, selection, projectId, onHide, title = 'Mood cover' }) => {
    const [operation, setOperation] = useState('cover');
    const [mood, setMood] = useState(null);
    const [note, setNote] = useState('');
    const [strength, setStrength] = useState(DEFAULT_STRENGTH);

    const isGapfill = operation === 'gapfill';
    const hasRange =
        selection?.startPos != null &&
        selection?.endPos != null &&
        selection.endPos > selection.startPos;

    const { jobs, isFetching, waiting, refresh } = useAiJobs({
        queryKey: ['project', projectId, 'covers'],
        queryFn: () => CoverService.listCoversAsync(projectId),
        enabled: Boolean(visible && projectId),
    });

    const reset = () => {
        setOperation('cover');
        setMood(null);
        setNote('');
        setStrength(DEFAULT_STRENGTH);
    };

    const create = useMutation({
        mutationFn: (body) => CoverService.createCoverAsync(projectId, body),
        onSuccess: () => {
            setMood(null);
            setNote('');
            setStrength(DEFAULT_STRENGTH);
            refresh();
        },
    });

    useEffect(() => {
        if (!visible) reset();
    }, [visible]);

    const handleHide = () => {
        reset();
        onHide();
    };

    const canSubmit = isGapfill ? hasRange : Boolean(mood);

    const handleSubmit = () => {
        if (!canSubmit || create.isPending) return;

        create.mutate({
            operation,
            timelineFrom: selection?.startPos ?? null,
            timelineTo: selection?.endPos ?? null,
            note: note || null,
            ...(isGapfill ? {} : { mood, strength }),
        });
    };

    return (
        <ModalDialog
            header={isGapfill ? __('Riempi il vuoto', 'soundbetween') : title}
            visible={visible}
            onHide={handleHide}
            onSubmit={handleSubmit}
            width={520}>
            <div className="moodCoverModal">
                <div className="moodCoverModal__moods">
                    {OPERATIONS.map(({ id, label }) => (
                        <button
                            key={id}
                            type="button"
                            className={`moodCoverModal__moodBadge${operation === id ? ' moodCoverModal__moodBadge--active' : ''}`}
                            onClick={() => setOperation(id)}>
                            {label}
                        </button>
                    ))}
                </div>

                <p className="moodCoverModal__hint">
                    {isGapfill
                        ? __('Riempie il tratto vuoto selezionato in modo coerente con la musica prima e dopo.', 'soundbetween')
                        : __('Reinterpreta il pezzo selezionato mantenendo melodia e struttura, con il mood scelto.', 'soundbetween')}
                </p>

                {!isGapfill && (
                    <>
                        <div className="moodCoverModal__moods">
                            {MOODS.map(({ id, label }) => (
                                <button
                                    key={id}
                                    type="button"
                                    className={`moodCoverModal__moodBadge${mood === id ? ' moodCoverModal__moodBadge--active' : ''}`}
                                    onClick={() => setMood(prev => prev === id ? null : id)}>
                                    {label}
                                </button>
                            ))}
                        </div>

                        <div className="moodCoverModal__strength">
                            <div className="moodCoverModal__strengthHead">
                                <label>{__('Intensità del mood', 'soundbetween')}</label>
                                <span>{Math.round(strength * 100)}%</span>
                            </div>
                            <input
                                type="range"
                                min="0.3"
                                max="0.9"
                                step="0.05"
                                value={strength}
                                onChange={e => setStrength(parseFloat(e.target.value))}
                            />
                            <div className="moodCoverModal__strengthLabels">
                                <span>{__('Più fedele', 'soundbetween')}</span>
                                <span>{__('Più trasformato', 'soundbetween')}</span>
                            </div>
                        </div>
                    </>
                )}

                {isGapfill && !hasRange && (
                    <p className="moodCoverModal__note--muted">
                        {__('Seleziona sulla timeline il tratto vuoto da riempire.', 'soundbetween')}
                    </p>
                )}

                <textarea
                    className="moodCoverModal__note"
                    placeholder={isGapfill
                        ? __('Cosa mettere nel vuoto (opzionale)…', 'soundbetween')
                        : __('Nota per l’AI (opzionale)…', 'soundbetween')}
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    rows={2}
                />

                <div className="moodCoverModal__actions">
                    <button
                        className="moodCoverModal__submit"
                        disabled={!canSubmit || create.isPending}
                        onClick={handleSubmit}>
                        <MagicAiIcon/>
                        {create.isPending
                            ? __('Invio…', 'soundbetween')
                            : (isGapfill ? __('Riempi il vuoto', 'soundbetween') : __('Genera cover', 'soundbetween'))}
                    </button>
                </div>

                <AiJobList
                    title={__('Cover del progetto', 'soundbetween')}
                    jobs={jobs}
                    loading={isFetching}
                    waiting={waiting}
                    onRefresh={refresh}
                    emptyLabel={__('Nessuna cover ancora.', 'soundbetween')}
                    renderLabel={(job) => (job.operation === 'gapfill'
                        ? __('Vuoto riempito', 'soundbetween')
                        : (job.mood || __('Cover', 'soundbetween')))}
                />
            </div>
        </ModalDialog>
    );
};

export default ModalMoodCover;
